import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  createDonationApi,
  startPaymentApi,
  confirmPaymentApi,
  rejectPaymentApi,
  startStripePaymentApi,
  cancelPaymentApi,
} from '../../services/payment/paymentApi';
import {
  DonationPayload,
  PaymentState,
} from '../../types/payment/paymentTypes';

const initialState: PaymentState = {
  payment: null,
  approvalUrl: null,
  loading: {
    creating: false,
    starting: false,
    confirming: false,
    rejecting: false,
  },
  error: null,
  successMessage: null,
};

// ----------------- DONATION -----------------
export const createDonation = createAsyncThunk(
  'payment/createDonation',
  async (data: DonationPayload, { rejectWithValue }) => {
    try {
      return await createDonationApi(data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Failed to create donation');
    }
  }
);




// ----------------- PAYMENT -----------------
export const startPayment = createAsyncThunk(
  'payment/startPayment',
  async (paymentId: number, { rejectWithValue }) => {
    try {
      return await startPaymentApi(paymentId);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Failed to start PayPal payment');
    }
  }
);

// redux/payment/paymentSlice.ts
export const cancelPayment = createAsyncThunk(
  'payment/cancelPayment',
  async (paymentId: number, { rejectWithValue }) => {
    try {
      return await cancelPaymentApi(paymentId);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Failed to cancel payment');
    }
  }
);

export const confirmPayment = createAsyncThunk(
  'payment/confirmPayment',
  async (
    { paymentId, paymentIdPaypal, payerId }: { paymentId: number; paymentIdPaypal: string; payerId: string },
    { rejectWithValue }
  ) => {
    try {
      return await confirmPaymentApi(paymentId, paymentIdPaypal, payerId);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Failed to confirm payment');
    }
  }
);

export const rejectPayment = createAsyncThunk(
  'payment/rejectPayment',
  async (paymentId: number, { rejectWithValue }) => {
    try {
      return await rejectPaymentApi(paymentId);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Failed to reject payment');
    }
  }
);


export const startStripePayment = createAsyncThunk(
  'payment/startStripePayment',
  async (paymentId: number, { rejectWithValue }) => {
    try {
      return await startStripePaymentApi(paymentId);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Failed to start Stripe payment');
    }
  }
);


// ----------------- SLICE -----------------

const paymentSlice = createSlice({
  name: 'payment',
  initialState,
  reducers: {
    clearPaymentState(state) {
      state.error = null;
      state.successMessage = null;
      state.payment = null;
      state.approvalUrl = null;
      state.loading = {
        creating: false,
        starting: false,
        confirming: false,
        rejecting: false,
      };
    },
    clearPaymentError(state) {
      state.error = null;
    },
    resetDonation(state) {
      state.payment = null;
      state.approvalUrl = null;
    },

  },
  extraReducers: (builder) => {
    builder
      // ----- createDonation -----
      .addCase(createDonation.pending, (state) => {
        state.loading.creating = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(createDonation.fulfilled, (state, action) => {
        state.loading.creating = false;
        state.payment = action.payload;
      })
      .addCase(createDonation.rejected, (state, action) => {
        state.loading.creating = false;
        state.error = action.payload as string;
      })



      // ----- startPayment -----
      .addCase(startPayment.pending, (state) => {
        state.loading.starting = true;
        state.error = null;
      })
      .addCase(startPayment.fulfilled, (state, action) => {
        state.loading.starting = false;
        state.approvalUrl = action.payload.approval_url;
      })
      .addCase(startPayment.rejected, (state, action) => {
        state.loading.starting = false;

        const payload = action.payload as any;
        if (typeof payload === "string") {
          state.error = payload;
        } else {
          state.error = payload?.detail || payload?.error || "Failed to start payment.";
        }
      })

      // ----- cancelPayment -----
      .addCase(cancelPayment.pending, (state) => {
        state.loading.rejecting = true; // استفاده مجدد از rejecting
        state.error = null;
      })
      .addCase(cancelPayment.fulfilled, (state, action) => {
        state.loading.rejecting = false;
        state.successMessage = action.payload.message || 'Payment canceled successfully.';
        state.payment = null; // پاک‌سازی اطلاعات پرداخت
      })
      .addCase(cancelPayment.rejected, (state, action) => {
        state.loading.rejecting = false;
        state.error = action.payload as string;
      })

      // ----- confirmPayment -----
      .addCase(confirmPayment.pending, (state) => {
        state.loading.confirming = true;
        state.error = null;
      })
      .addCase(confirmPayment.fulfilled, (state) => {
        state.loading.confirming = false;
        state.successMessage = 'Payment confirmed successfully.';
      })
      .addCase(confirmPayment.rejected, (state, action) => {
        state.loading.confirming = false;
        state.error = action.payload as string;
      })

      // ----- rejectPayment -----
      .addCase(rejectPayment.pending, (state) => {
        state.loading.rejecting = true;
        state.error = null;
      })
      .addCase(rejectPayment.fulfilled, (state) => {
        state.loading.rejecting = false;
        state.successMessage = 'Payment rejected successfully.';
      })
      .addCase(rejectPayment.rejected, (state, action) => {
        state.loading.rejecting = false;
        state.error = action.payload as string;
      })

      // ----- startStripePayment -----
      .addCase(startStripePayment.pending, (state) => {
        state.loading.starting = true;
        state.error = null;
      })
      .addCase(startStripePayment.fulfilled, (state, action) => {
        state.loading.starting = false;
        // You may want to store the client_secret if needed for later use in the frontend component
        state.approvalUrl = null; // PayPal-specific — reset it
      })
      .addCase(startStripePayment.rejected, (state, action) => {
        state.loading.starting = false;

        const payload = action.payload as any;
        if (typeof payload === "string") {
          state.error = payload;
        } else {
          state.error = payload?.detail || payload?.error || "Failed to start Stripe payment.";
        }
      });

  },
});

export const { clearPaymentState, resetDonation, clearPaymentError } = paymentSlice.actions;
export default paymentSlice.reducer;
